<admin-page-simple-content
    [pageError]="pageError"
    [pageStatus]="pageStatus"
    [title]="'Manage Admin User'"
    [icon]="'save'"
>
    @if (user) {
        <div class="content">
            @if (pageStatus === "saveError" && pageError) {
                <admin-error [error]="pageError"></admin-error>
            }
            <div class="header">
                <img [alt]="'Profile picture of ' + user.user.email" [src]="user.user.picture" class="user-avatar" />
                <h2 adminCopyToClipboard>{{ user.user.email }}</h2>
                @if (user.user.isMfaEnabled) {
                    <mat-icon tabindex="0"
                              [matTooltip]="'Multi-factor Authentication enabled'">security
                    </mat-icon>
                }
                @if (!user.user.isMfaEnabled) {
                    <mat-icon tabindex="0"
                              class="warning"
                              [matTooltip]="'Multi-factor Authentication disabled'">warning
                    </mat-icon>
                }
            </div>
            <section class="info">
                <h2>Information</h2>

                <div class="admin-field">
                    <div class="label">Auth0 ID</div>
                    <div class="value">
                        <code adminCopyToClipboard>{{ user.user.id }}</code>
                    </div>
                </div>

                @if (user.user.lastLoginDate) {
                    <div class="admin-field">
                        <div class="label">Last login</div>
                        <div class="value">
                            <span>{{ user.user.lastLoginDate | date: "full" }} ({{ user.user.lastLoginDate | fromNow }})</span>
                        </div>
                    </div>
                }
            </section>
            <section class="global-roles">
                <h2>Global Roles</h2>
                <div class="roles">
                    <mat-chip-listbox aria-label="User roles">
                        @for (role of user.globalRoles; track role) {
                            <mat-chip-option selectable="false" [removable]="canManageUser()"
                                             (removed)="removeGlobalRole(user, role)">
                                {{ role }}
                                @if (canManageUser()) {
                                    <button
                                        matChipRemove
                                        [disabled]="pageStatus === 'saving'"
                                        [attr.aria-label]="'Remove role: ' + role"
                                    >
                                        <mat-icon>cancel</mat-icon>
                                    </button>
                                }
                            </mat-chip-option>
                        }
                    </mat-chip-listbox>
                    @if (canManageUser()) {
                        <button mat-icon-button (click)="openAddGlobalRole(user)" aria-label="Add global role">
                            <mat-icon>add</mat-icon>
                        </button>
                    }
                </div>
            </section>
            <section class="downloads">
                <h2>Authorized Downloads</h2>
                <div class="download-list">
                    <mat-chip-listbox aria-label="User allowed downloads">
                        @for (download of user.downloads; track download) {
                            <mat-chip-option selectable="false" [removable]="canManageUser()"
                                             (removed)="removeDownload(user, download)">
                                {{ download }}
                                @if (canManageUser()) {
                                    <button
                                        matChipRemove
                                        [disabled]="pageStatus === 'saving'"
                                        [attr.aria-label]="'Remove download: ' + download"
                                    >
                                        <mat-icon>cancel</mat-icon>
                                    </button>
                                }
                            </mat-chip-option>
                        }
                    </mat-chip-listbox>
                    <form #form="ngForm" (ngSubmit)="addDownload(downloadValue)">
                        <mat-form-field class="dense-2">
                            <mat-label>New download</mat-label>
                            <input
                                matInput
                                name="downloadValue"
                                [(ngModel)]="downloadValue"
                                [matAutocomplete]="auto"
                                required
                            />
                        </mat-form-field>
                        <mat-autocomplete #auto="matAutocomplete">
                            @for (download of filterDownloads(availableDownloads); track download) {
                                <mat-option [value]="download.path">
                                    {{ download.path }}
                                </mat-option>
                            }
                        </mat-autocomplete>
                        <button mat-stroked-button type="submit" [disabled]="form.invalid || pageStatus === 'saving'">
                            <mat-icon>add</mat-icon>
                            Add
                        </button>
                    </form>
                </div>
            </section>
            @if (user.subscriptionsRoles.length) {
                <section class="subscriptions-roles">
                    <h2>Subscriptions Roles</h2>
                    <ul>
                        @for (subscriptionRoles of user.subscriptionsRoles; track subscriptionRoles) {
                            <li>
                                Subscription: <a
                                tabindex="0"
                                [routerLink]="['/subscriptions', subscriptionRoles.subscriptionId, 'authorizedUsers']">{{ subscriptionRoles.subscriptionId }}</a>
                                <mat-chip-set>
                                    @for (role of subscriptionRoles.roles; track role) {
                                        <mat-chip>
                                            {{ role }}
                                        </mat-chip>
                                    }
                                </mat-chip-set>
                            </li>
                        }
                    </ul>
                </section>
            }
            @if (user.storesRoles.length) {
                <section class="stores-roles">
                    <h2>Stores Roles</h2>
                    <ul>
                        @for (storeRoles of user.storesRoles; track storeRoles) {
                            <li>
                                Store: <a
                                tabindex="0"
                                [routerLink]="['/stores', storeRoles.storeId, 'settings', 'authorized-users']">{{ storeRoles.storeId }}</a>
                                <mat-chip-set>
                                    @for (role of storeRoles.roles; track role) {
                                        <mat-chip>
                                            {{ role }}
                                        </mat-chip>
                                    }
                                </mat-chip-set>
                            </li>
                        }
                    </ul>
                </section>
            }
            @if (user.librariesRoles.length) {
                <section class="libraries-roles">
                    <h2>Libraries Roles</h2>
                    <ul>
                        @for (libraryRoles of user.librariesRoles; track libraryRoles) {
                            <li>
                                Library: <a
                                tabindex="0"
                                [routerLink]="['/libraries', libraryRoles.libraryOwner, 'authorizedUsers']">{{ libraryRoles.libraryOwner }}</a>
                                <mat-chip-set>
                                    @for (role of libraryRoles.roles; track role) {
                                        <mat-chip>
                                            {{ role }}
                                        </mat-chip>
                                    }
                                </mat-chip-set>
                            </li>
                        }
                    </ul>
                </section>
            }
            @if (user.fulfillersRoles.length) {
                <section class="fulfillers-roles">
                    <h2>Fulfillers Roles</h2>
                    <ul>
                        @for (fulfillerRoles of user.fulfillersRoles; track fulfillerRoles) {
                            <li>
                                Fulfiller: <a tabindex="0"
                                              [routerLink]="['/fulfillers', fulfillerRoles.fulfillerId, 'authorizedUsers']">{{ fulfillerRoles.fulfillerId }}</a>
                                <mat-chip-set>
                                    @for (role of fulfillerRoles.roles; track role) {
                                        <mat-chip>
                                            {{ role }}
                                        </mat-chip>
                                    }
                                </mat-chip-set>
                            </li>
                        }
                    </ul>
                </section>
            }
            <section class="user-actions">
                <h2>Actions</h2>
                <div class="actions">
                    <button mat-raised-button color="warn" [disabled]="hasAnyRole(user) || pageStatus === 'saving'"
                            (click)="deleteUser(user)">
                        Delete User
                    </button>
                    @if (hasAnyRole(user)) {
                        <span tabindex="0"
                              [matTooltip]="'You need to remove all roles before deleting this user'">
        <mat-icon color="warn">warning</mat-icon>
      </span>
                    }
                </div>
            </section>
        </div>
    }
</admin-page-simple-content>
